import React, { useEffect } from 'react'; // ✅ Importamos useEffect
import { useParams } from 'react-router-dom';
import { executeResolutionApi } from '@api/gameApi';

// Hooks
import useGame from '@/hooks/useGame'; // ✅ Usamos nuestro nuevo y limpio hook
import useGameStore from "@store/useGameStore";
import useSelectionStore from "@store/useSelectionStore";
import useMetadataStore from "@store/useMetadataStore"; // Import metadata store
import {GameStatus} from '@utils/enums'; // ✅ Importamos el enum de estados del juego
// Componentes y Gestores de UI
import MainMenu from "@components/ui/game/main-menu/MainMenu";
import PlayerStatusPanel from "@components/game/panels/PlayerStatusPanel";
import MapContainer from "@containers/MapContainer";
import PhaseContainer from "@containers/PhaseContainer";
import InfoPanel from "@components/game/panels/InfoPanel/InfoPanel";
import DeployableUnitsPanel from "@components/game/panels/DeployableUnitsPanel";
import GameStatusButtonPanel from "@components/game/panels/GameStatusButtonPanel";
import Loader from '@/components/ui/general/loader/Loader';
import CinematicPanel from '@components/game/panels/CinematicPanel';

import "./GameView.css";

function GameView() {
    // 1. Llamamos a nuestro hook. Él se encarga de toda la lógica de carga.
    const { fetchGameById } = useGame();
    const { gameId } = useParams(); // ✅ Obtenemos el gameId aquí
    
    // Load metadata when entering game view (if not already loaded)
    useEffect(() => {
        const loadMetadata = async () => {
            const metadataStore = useMetadataStore.getState();
            if (!metadataStore.isReady()) {
                // console.log('[GameView] Loading game metadata...');
                try {
                    await metadataStore.fetchMetadata();
                    // console.log('[GameView] Metadata loaded successfully');
                } catch (error) {
                    console.error('[GameView] Failed to load metadata:', error);
                    // Continue anyway - game can still work without some features
                }
            }
        };
        
        loadMetadata();
    }, []); // Run once on mount
    
    useEffect(() => {
        if (gameId) {
            fetchGameById(gameId); // ✅ Pasamos el gameId al hook
        }
    }, [gameId, fetchGameById]);

    // 2. Subscribe to store data atomically for efficiency
    const game = useGameStore((state) => state.game);
    const loading = useGameStore((state) => state.loading);
    const error = useGameStore((state) => state.error);
    const isDeployPhase = useGameStore((state) => state.isDeployPhase);

    // Derive resolving and resolved from game status
    const isResolving = game?.status === GameStatus.Resolving;
    const isResolved = game?.status === GameStatus.Resolved;

    // When game enters RESOLVING, trigger resolution execution
    // TEMPORARILY DISABLED FOR DEBUGGING
    useEffect(() => {
        if (isResolving && game?.id) {
            // console.log("Game is RESOLVING - AUTO-EXECUTE DISABLED FOR DEBUGGING");
            // console.log("To manually trigger resolution, run in console:");
            // console.log("executeResolutionApi(" + game.id + ")");
            
            // COMMENTED OUT FOR DEBUGGING - Uncomment to re-enable auto-execution
            // Start the resolution process - backend handles it in chunks
            const startResolution = async () => {
                try {
                    await executeResolutionApi(game.id);
                    console.log("Resolution chunk processed successfully");
                    
                    // Check if we're still resolving and need to continue
                    const { game: currentGame } = useGameStore.getState();
                    if (currentGame?.status === GameStatus.Resolving) {
                        // Backend is still processing, call again after 2 seconds
                        console.log("Still resolving, calling execute-resolution again...");
                        setTimeout(startResolution, 2000);
                    } else {
                        console.log("Resolution complete, status:", currentGame?.status);
                    }
                } catch (err) {
                    // Only log error if it's not the expected "already resolving" error
                    if (err?.response?.status !== 409) {
                        console.error("Error executing resolution:", err);
                    }
                    
                    // For now, still poll with execute-resolution as fallback
                    console.log("Starting fallback polling...");
                    const pollInterval = setInterval(async () => {
                        try {
                            console.log("Polling resolution status...");
                            await executeResolutionApi(game.id);
                            
                            // Check if status changed (will be handled by store update)
                            const { game: currentGame } = useGameStore.getState();
                            if (currentGame?.status !== GameStatus.Resolving) {
                                console.log("Game is no longer RESOLVING, stopping poll");
                                clearInterval(pollInterval);
                            }
                        } catch (err) {
                            // Silently ignore 409 errors (already processing)
                            if (err?.response?.status !== 409) {
                                console.error("Error polling resolution:", err);
                            }
                        }
                    }, 2000); // Poll every 2 seconds
                    
                    // Store interval ID for cleanup
                    return () => clearInterval(pollInterval);
                }
            };
            
            startResolution();
        }
    }, [isResolving, game?.id, fetchGameById]);



    // --- Lógica de Renderizado ---
    if (loading) return <Loader message="Loading Game..." />;
    if (error) return <div><p style={{ color: "red" }}>Error: {error}</p></div>;
    if (!game) return <Loader message="Waiting for game data..." />;

    // Get teams from the game players array
    const team1 = game.players?.filter(p => p.pivot?.team === 1) || [];
    const team2 = game.players?.filter(p => p.pivot?.team === 2) || [];

    return (
        <div className="game-view">
            <MainMenu />
            <PhaseContainer />

            {/* Overlay while backend calculates resolution */}
            {isResolving && <Loader message="Resolving turn..." isOverlay={true} />}


            <div className="game-view-layout">
                <div className="left-column">
                     <PlayerStatusPanel team1={team1} team2={team2}/>
                    {isDeployPhase && (
                        <DeployableUnitsPanel />
                    )}
                    <GameStatusButtonPanel />                  
                </div>
                <div className="main-column">
                    <MapContainer />
                </div>
                <div className="right-column" >
                    <InfoPanel />
                </div>
                
            </div>
             {isResolved && <CinematicPanel />}
        </div>
    );
}

export default GameView;